import { useCallback, useMemo, useState } from "react";

import { DispatchType } from "@bridgesplit/react";
import { All, ALL_TOKENS } from "@bridgesplit/utils";

/**
 * Chunked Pagination Controller
 *
 * This controller implements an over-fetching strategy for pagination where:
 * - Client sees `pageSize` records per page
 * - Backend receives `pageSize * CACHE_MULTIPLIER` records per request (chunk size)
 * - TParams must have `page` and `pageSize` properties that get replaced with chunk-based values
 *
 * How it works:
 * 1. Client page/pageSize control what the user sees
 * 2. Backend pagination uses chunkIndex (page / CACHE_MULTIPLIER) and fetchSize (pageSize * CACHE_MULTIPLIER)
 * 3. When user approaches cache boundary ((page + CHUNK_BUFFER) * pageSize >= cached data),
 *    chunkIndex increments to trigger refetch
 * 4. This ensures smooth pagination while minimizing API calls
 *
 * Example: User sees 5 records per page, but backend fetches 20 records (4x cache multiplier). When user is 5 records away from the end of the page, the backend will fetch the next chunk.
 */
export interface ChunkedPaginationState<TData, TParams> {
    page: number;
    pageSize: number;
    setPage: DispatchType<number>;
    setPageSize: DispatchType<number>;

    backendPagination: TParams; //Params sent to backend with page and pageSize replaced to be chunkIndex and fetchSize
    clientPagination: TParams; //Params sent to client with page and pageSize replaced to be chunkIndex and fetchSize

    cacheMultiplier: number;
    chunkBuffer: number;
    shouldRefetch: boolean;

    visibleData: (data: TData[] | undefined) => TData[] | undefined;
    resetPagination: () => void;
}

export function useChunkedPaginationController<TData, TParams extends { page: number; pageSize: number }>(
    baseParams: Omit<TParams, "page" | "pageSize">,
    defaultPageSize = 5,
    cacheMultiplier = 4,
    chunkBuffer = 1
): ChunkedPaginationState<TData, TParams> {
    const [page, setPage] = useState(0); //Client page
    const [pageSize, setPageSize] = useState(defaultPageSize); //Client page size

    const chunkIndex = getChunkIndex(page, cacheMultiplier);
    const fetchSize = pageSize * cacheMultiplier;

    const backendPagination = useMemo(() => {
        return {
            ...baseParams,
            page: chunkIndex,
            pageSize: fetchSize
        } as TParams;
    }, [baseParams, chunkIndex, fetchSize]);

    const shouldRefetch = (page + chunkBuffer) * pageSize >= fetchSize; //Used for logging. The actual invalidation is from chunkIndex and the RTK query cache.

    const visibleData = useCallback(
        (data: TData[] | undefined) => {
            if (!data) return undefined;
            const pageInChunk = page % cacheMultiplier;
            const start = pageInChunk * pageSize;
            return data.slice(start, start + pageSize);
        },
        [page, pageSize, cacheMultiplier]
    );

    const resetPagination = useCallback(() => {
        setPage(0);
    }, []);

    const clientPagination = useMemo(() => {
        return {
            pageSize,
            page,
            ...baseParams
        } as TParams;
    }, [pageSize, page, baseParams]);

    return {
        page,
        pageSize,
        setPage,
        setPageSize,
        backendPagination,
        clientPagination,
        cacheMultiplier,
        chunkBuffer,
        shouldRefetch,
        visibleData,
        resetPagination
    };
}

function getChunkIndex(page: number, cacheMultiplier: number) {
    return Math.floor(page / cacheMultiplier);
}

//Backend handles ALL_TOKENS as undefined
export function getPrincipalMints(principalMint: string | undefined | All) {
    if (!principalMint || principalMint === ALL_TOKENS) {
        return undefined;
    }
    return [principalMint];
}
